// src/components/network/messaging/NewMessage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, getDocs, addDoc, 
  serverTimestamp, doc, getDoc, setDoc 
} from 'firebase/firestore';
import { Search, Building, User, X, Send, ArrowLeft } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const NewMessage = ({ onSelectConversation, onBack }) => {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Search for businesses
  const handleSearch = async () => {
    if (!searchTerm.trim() || !currentUser) return;
    
    try {
      setLoading(true);
      
      // Query businesses by name
      const businessesQuery = query(
        collection(db, 'businesses'),
        where('nameKeywords', 'array-contains', searchTerm.toLowerCase())
      );
      
      const businessesSnapshot = await getDocs(businessesQuery);
      
      const businesses = businessesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'business'
        }))
        .filter(business => business.id !== currentUser.uid); // Filter out current user's business
      
      setSearchResults(businesses);
    } catch (err) {
      console.error('Error searching businesses:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Send a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() || !selectedBusiness || !currentUser) return;
    
    try {
      setSending(true);
      
      // Check if conversation already exists
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', currentUser.uid)
      );
      
      const conversationsSnapshot = await getDocs(conversationsQuery);
      
      let existingConversation = null;
      
      conversationsSnapshot.forEach(doc => {
        const conversation = doc.data();
        if (conversation.participants.includes(selectedBusiness.id)) {
          existingConversation = {
            id: doc.id,
            ...conversation
          };
        }
      });
      
      let conversationId;
      
      if (existingConversation) {
        // Use existing conversation
        conversationId = existingConversation.id;
        
        // Update conversation
        await setDoc(doc(db, 'conversations', conversationId), {
          lastMessage: messageText.trim(),
          lastMessageAt: serverTimestamp(),
          lastMessageSenderId: currentUser.uid,
          [`unreadCount.${selectedBusiness.id}`]: (existingConversation.unreadCount?.[selectedBusiness.id] || 0) + 1
        }, { merge: true });
      } else {
        // Create new conversation
        const conversationData = {
          participants: [currentUser.uid, selectedBusiness.id],
          createdAt: serverTimestamp(),
          lastMessage: messageText.trim(),
          lastMessageAt: serverTimestamp(),
          lastMessageSenderId: currentUser.uid,
          unreadCount: {
            [selectedBusiness.id]: 1,
            [currentUser.uid]: 0
          }
        };
        
        const conversationRef = await addDoc(collection(db, 'conversations'), conversationData);
        conversationId = conversationRef.id;
      }
      
      // Add message
      await addDoc(collection(db, 'messages'), {
        conversationId,
        senderId: currentUser.uid,
        text: messageText.trim(),
        createdAt: serverTimestamp(),
        read: false
      });
      
      // Get user info for the conversation object
      let userInfo = { id: currentUser.uid, name: 'Me' };
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userInfo = {
            id: currentUser.uid,
            name: userData.displayName || userData.email,
            type: 'user',
            email: userData.email,
            photoURL: userData.photoURL || null
          };
        }
      } catch (err) {
        console.error('Error fetching user info:', err);
      }
      
      // Build conversation object for parent component
      const conversation = {
        id: conversationId,
        participants: [currentUser.uid, selectedBusiness.id],
        lastMessage: messageText.trim(),
        lastMessageAt: new Date(),
        participant: selectedBusiness,
        unreadCount: 0
      };
      
      // Notify parent component
      onSelectConversation(conversation);
      
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };
  
  // Effect to run search when term changes
  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      const delayDebounce = setTimeout(() => {
        handleSearch();
      }, 500);
      
      return () => clearTimeout(delayDebounce);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);
  
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-gray-200 flex items-center">
        <button 
          onClick={onBack}
          className="p-1 mr-2 text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={20} />
        </button>
        <h3 className="font-medium text-gray-900">New Message</h3>
      </div>
      
      <div className="flex-1 flex flex-col">
        {/* Search or selected recipient */}
        {!selectedBusiness ? (
          <div className="p-4 border-b border-gray-200">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Search for a business..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex p-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : searchResults.length === 0 && searchTerm.trim().length >= 2 ? (
              <div className="p-4 text-center">
                <p className="text-gray-500">No businesses found</p>
                <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {searchResults.map(business => (
                  <div 
                    key={business.id} 
                    className="flex items-center p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedBusiness(business)}
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                      {business.businessLogoUrl ? (
                        // src/components/network/messaging/NewMessage.jsx (continued)
                        <img 
                          src={business.businessLogoUrl} 
                          alt={business.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                          <Building size={18} />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{business.name}</h4>
                      {business.industry && (
                        <p className="text-sm text-gray-500 truncate">{business.industry}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 border-b border-gray-200 flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
              {selectedBusiness.businessLogoUrl ? (
                <img 
                  src={selectedBusiness.businessLogoUrl} 
                  alt={selectedBusiness.name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                  <Building size={18} />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 truncate">{selectedBusiness.name}</h4>
              {selectedBusiness.industry && (
                <p className="text-sm text-gray-500 truncate">{selectedBusiness.industry}</p>
              )}
            </div>
            
            <button 
              onClick={() => setSelectedBusiness(null)}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <X size={18} />
            </button>
          </div>
        )}
        
        {/* Message composer (only shown when recipient is selected) */}
        {selectedBusiness && (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 p-4">
              <p className="text-sm text-gray-500 text-center">
                Start a conversation with {selectedBusiness.name}
              </p>
            </div>
            
            <div className="p-3 border-t border-gray-200">
              <form onSubmit={handleSendMessage} className="flex">
                <input
                  type="text"
                  className="flex-1 border border-gray-200 rounded-l-lg px-3 py-2 focus:outline-none focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  placeholder="Type a message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={sending || !messageText.trim()}
                  className="px-4 py-2 bg-[#190192] text-white rounded-r-lg hover:bg-[#2C0DB5] disabled:bg-gray-400"
                >
                  {sending ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div>
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewMessage;